import React, { useEffect, useReducer, useRef, useState } from 'react';
import { useAppState } from '../../../overmind';
import { PagePath } from '../../../navigation/navigation.enums';
import { PageLoaderLayer } from '../../../components/general/loading-state/loader-layers/page-loader-layer/page-loader-layer';
import { JsonViewer } from '../../../components/json-viewer/json-viewer';
import * as store from './chat.store';
import { createChatStream } from '../../../overmind/actions/ai/ai-chat';
import { PageHeader, Button, TextArea } from '@keplerco/core';
import styles from './chat.module.css';
import classNames from 'classnames';

export function ChatPage(): JSX.Element {
  const { companyVariables } = useAppState();

  const [state, dispatch] = useReducer(store.reducer, store.initialState);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const eventSourceRef = useRef<EventSource | null>(null);

  const [inputMessage, setInputMessage] = useState('');
  const [expandedTools, setExpandedTools] = useState<Record<string, boolean>>({});

  function toggleToolExpansion(toolId: string) {
    setExpandedTools(prev => ({
      ...prev,
      [toolId]: !prev[toolId],
    }));
  }

  useEffect(() => {
    return () => {
      if (!eventSourceRef.current) return;
      eventSourceRef.current.close();
    };
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [state.messages, state.messageChunks, state.isStreaming]);

  function scrollToBottom() {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }

  function handleInputChange(event: React.ChangeEvent<HTMLTextAreaElement>) {
    setInputMessage(event.target.value);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key !== 'Enter' || event.shiftKey) return;
    event.preventDefault();
    handleSendMessage(inputMessage);
    setInputMessage('');
  }

  function handleSendMessage(message: string) {
    if (!message.trim() || state.isStreaming) return;

    const userMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: message,
    };

    dispatch({ type: store.ChatActionTypes.AddMessage, payload: userMessage });
    dispatch({ type: store.ChatActionTypes.SetIsStreaming, payload: true });

    // Create event source for streaming
    eventSourceRef.current = createChatStream(
      message, // Use the raw message text
      // Message handler
      data => {
        // Check if we received an error message
        if (data.startsWith('Cannot Convert DocumentType')) {
          console.error('Error in streaming:', data);
          // Don't update the UI with error messages
          return;
        }

        // Add the message content as a text chunk
        dispatch({
          type: store.ChatActionTypes.AddMessageChunk,
          payload: {
            type: 'text',
            content: data,
          },
        });
      },
      // Tool use handler
      data => {
        console.log('Tool use:', data);
        // Parse the tool use data if it's not already an object
        try {
          const toolUseData = typeof data === 'string' ? JSON.parse(data) : data;
          // Add the tool usage as a tool chunk
          dispatch({
            type: store.ChatActionTypes.AddMessageChunk,
            payload: {
              type: 'tool',
              tool: toolUseData.tool,
              input: toolUseData,
            },
          });
        } catch (error) {
          console.error('Error parsing tool use data:', error);
        }
      },
      // Tool response handler
      (data: string) => {
        console.log('Tool response:', data);
        // You could display tool responses in the UI here if desired
      },
      // Error handler
      (error: string) => {
        console.error('Error in chat stream:', error);
        dispatch({ type: store.ChatActionTypes.SetError, payload: 'Error in chat stream' });
        dispatch({ type: store.ChatActionTypes.SetIsStreaming, payload: false });

        // Combine all text chunks to form the complete message content
        const textContent = state.messageChunks
          .filter(chunk => chunk.type === 'text')
          .map(chunk => chunk.content)
          .join('');

        // Add the final assistant message to the conversation
        if (textContent) {
          const assistantMessage = {
            id: `assistant-${Date.now()}`,
            role: 'assistant',
            content: textContent,
          };
          dispatch({ type: store.ChatActionTypes.AddMessage, payload: assistantMessage });
        }
      },
      // Done handler
      () => {
        console.log('Chat stream done');
        dispatch({ type: store.ChatActionTypes.SetIsStreaming, payload: false });

        // Combine all text chunks to form the complete message content
        const textContent = state.messageChunks
          .filter(chunk => chunk.type === 'text')
          .map(chunk => chunk.content)
          .join('');

        // Add the final assistant message to the conversation
        if (textContent) {
          const assistantMessage = {
            id: `assistant-${Date.now()}`,
            role: 'assistant',
            content: textContent,
          };
          dispatch({ type: store.ChatActionTypes.AddMessage, payload: assistantMessage });
        }
      },
      // Token handler for streaming individual tokens
      token => {
        // Add the token to the current token stream
        dispatch({
          type: store.ChatActionTypes.AddToken,
          payload: token,
        });
        // Force a scroll to bottom to ensure the new token is visible
        setTimeout(scrollToBottom, 0);
      }
    );
  }

  return (
    <PageLoaderLayer path={PagePath.aiManagementChat}>
      <div className="wrapper stack">
        <PageHeader
          breadcrumbs={[
            { name: 'AI management', url: `${PagePath.aiManagementBase}${PagePath.aiManagementAgents.replace(':companySlug', companyVariables.slug!)}` },
            { name: 'Chat', url: location.pathname },
          ]}
          title="Chat"
          subtitle="Chat with the AI assistant"
          divider
        />

        <div className={styles.chatContainer}>
          <div className={styles.chatMessages}>
            {!state.messages.length && !state.isStreaming ? (
              <div className={styles.emptyChat}>
                <h5>Start a conversation with the AI assistant</h5>
                <p>Ask about the weather or any other topic</p>
              </div>
            ) : (
              <React.Fragment>
                {/* user messages */}
                {state.messages.map(message => {
                  const isUser = message.role === 'user';
                  const isAssistant = message.role === 'assistant';

                  return (
                    <div key={message.id} className={classNames(styles.chatMessage, { [styles.userMessage]: isUser, [styles.assistantMessage]: isAssistant })}>
                      <div className="card">
                        <div className={styles.messageHeader}>
                          <div className={styles.avatar}>{isUser ? 'U' : 'A'}</div>
                          <div className="bold">{isUser ? 'You' : 'Assistant'}</div>
                        </div>
                        <div className={styles.messageContent}>{message.content}</div>
                      </div>
                    </div>
                  );
                })}

                {/* assistant messages */}
                <div className={classNames(styles.chatMessage, styles.assistantMessage)}>
                  <div className="card">
                    <div className={styles.messageHeader}>
                      <div className={styles.avatar}>A</div>
                      <div className="bold">Assistant {state.isStreaming && <span className={styles.typingIndicator}>typing...</span>}</div>
                    </div>
                    <div className={styles.messageContent}>
                      {/* Display each tool usage chunk in its own div */}
                      {state.messageChunks.map((chunk, index) => {
                        // Only render tool usage chunks here since text is handled by currentTokens
                        if (chunk.type === 'text') {
                          return (
                            <div key={`chunk-${index}`}>
                              <div style={{ whiteSpace: 'pre-wrap' }}>
                                {chunk.content || ''}
                                {state.isStreaming && state.currentMessageIndex === index && <span className={styles.cursor}></span>}
                              </div>
                            </div>
                          );
                        } else {
                          return (
                            <div key={`chunk-${index}`} style={{ borderRadius: 4 }}>
                              <div className={styles.toolUsageHeader} onClick={() => toggleToolExpansion(`tool-${index}`)}>
                                <span className={classNames(styles.toolArrow, { [styles.expanded]: expandedTools[`tool-${index}`] })}></span>
                                Using tool: {chunk.tool}
                              </div>

                              <div className={classNames(styles.toolData, { [styles.collapsed]: !expandedTools[`tool-${index}`] })}>
                                <JsonViewer hideRawToggle={true} data={chunk.input} isPreview={false} />
                              </div>
                            </div>
                          );
                        }
                      })}
                    </div>
                  </div>
                </div>
              </React.Fragment>
            )}

            <div ref={messagesEndRef} />
          </div>

          <div className={styles.chatInput}>
            <TextArea label="Message" value={inputMessage} onChange={handleInputChange} onKeyDown={handleKeyDown} disabled={state.isStreaming} responsive />

            <div>
              <Button
                type="button"
                onClick={() => {
                  handleSendMessage(inputMessage);
                  setInputMessage('');
                }}
                disabled={!inputMessage.trim()}
                isLoading={state.isStreaming}
              >
                Send
              </Button>
            </div>
          </div>
        </div>
      </div>
    </PageLoaderLayer>
  );
}
